import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * WorkspaceSetupBanner - Phase 3.1 Industrial Onboarding
 *
 * Displays workspace status with industrial stage language:
 * - Amber banner when not operational (<100%)
 * - Green badge when operational (100%)
 */
const WorkspaceSetupBanner = ({
  setupComplete,
  setupLink,
  completionPercent = 0,
  discipline = 'machine', // 'machine', 'printer', 'laser'
  userId = null
}) => {
  const dismissKey = userId ? `banner_dismissed_${userId}` : null;
  const [dismissed, setDismissed] = useState(() => {
    if (!dismissKey) return false;
    return localStorage.getItem(dismissKey) === 'true';
  });

  const handleDismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, 'true');
    setDismissed(true);
  };

  // If fully operational, show green badge
  if (setupComplete && completionPercent >= 100) {
    return (
      <div
        data-testid="workspace-operational-badge"
        className="bg-black/80 backdrop-blur-sm border border-emerald-500/50 rounded-sm p-3"
      >
        <div className="flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-400" />
          <span className="text-emerald-300 font-semibold">Operational</span>
          <span className="text-emerald-200/70 text-sm">– Workspace Ready</span>
        </div>
      </div>
    );
  }

  // If dismissed, render nothing
  if (dismissed) return null;

  // If no machine at all, show setup required
  if (!setupComplete) {
    return (
      <div
        data-testid="workspace-setup-banner"
        className="bg-black/80 backdrop-blur-sm border border-amber-500/50 rounded-sm p-4"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-500/20 border border-amber-500/50 rounded-sm flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <div className="text-white font-semibold mb-1">Workspace not operational</div>
              <div className="text-sm text-amber-100/80">
                Complete setup to unlock full tool capability.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={setupLink}>
              <Button
                data-testid="complete-setup-btn"
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold flex items-center gap-2 whitespace-nowrap"
              >
                Complete Setup
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <button
              onClick={handleDismiss}
              className="p-1 text-amber-400/60 hover:text-amber-400 transition-colors"
              title="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Machine exists but not fully complete - show continue setup
  return (
    <div
      data-testid="workspace-setup-banner"
      className="bg-black/80 backdrop-blur-sm border border-amber-500/50 rounded-sm p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-500/20 border border-amber-500/50 rounded-sm flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <div className="text-white font-semibold mb-1">Workspace not operational</div>
            <div className="text-sm text-amber-100/80">
              Complete setup to unlock full tool capability.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={setupLink}>
            <Button
              data-testid="continue-setup-btn"
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold flex items-center gap-2 whitespace-nowrap"
            >
              Continue Setup
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <button
            onClick={handleDismiss}
            className="p-1 text-amber-400/60 hover:text-amber-400 transition-colors"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * getWorkspaceStage - Calculate industrial stage from completion percentage
 * 
 * 0-25%  → Build
 * 26-60% → Tune
 * 61-99% → Validate
 * 100%   → Operational
 */
export const getWorkspaceStage = (percent) => {
  if (percent >= 100) return { stage: 4, label: 'Operational', color: 'text-emerald-400' };
  if (percent >= 61) return { stage: 3, label: 'Validate', color: 'text-amber-400' };
  if (percent >= 26) return { stage: 2, label: 'Tune', color: 'text-amber-400' };
  return { stage: 1, label: 'Build', color: 'text-amber-400' };
};

/**
 * WorkspaceStatusLabel - Display stage-based status label
 */
export const WorkspaceStatusLabel = ({ percent, showPercent = true }) => {
  const { label, color } = getWorkspaceStage(percent);
  const isOperational = percent >= 100;
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-[#a3a3a3] text-sm">Workspace Status:</span>
      <span className={`font-semibold ${color}`}>
        {label}
        {showPercent && !isOperational && ` (${percent}%)`}
      </span>
      {isOperational && (
        <CheckCircle className="w-4 h-4 text-emerald-400" />
      )}
    </div>
  );
};

export default WorkspaceSetupBanner;
